import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Tag, Progress, Tooltip, Button, Space, Modal, Badge, Spin, Popconfirm, message, Select, Divider } from 'antd';
import { 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  DeleteOutlined,
  StopOutlined
} from '@ant-design/icons';
import { executionAPI, usersAPI } from '../services/api';
import ExecutionLogViewer from './ExecutionLogViewer';
import './JobMonitor.css';

const JobMonitor = ({ selectedClient, refreshInterval = 5000 }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState(selectedClient || null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const intervalRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(240);

  const statusConfig = {
    pending: { 
      color: 'orange', 
      icon: <ClockCircleOutlined />, 
      text: 'Pending' 
    },
    running: { 
      color: 'blue', 
      icon: <PlayCircleOutlined />, 
      text: 'Running' 
    },
    waiting: { 
      color: 'gold', 
      icon: <ClockCircleOutlined />, 
      text: 'Waiting' 
    },
    completed: { 
      color: 'green', 
      icon: <CheckCircleOutlined />, 
      text: 'Completed' 
    },
    failed: { 
      color: 'red', 
      icon: <ExclamationCircleOutlined />, 
      text: 'Failed' 
    },
    resumed: { 
      color: 'purple', 
      icon: <PlayCircleOutlined />, 
      text: 'Resumed' 
    },
    terminated: { 
      color: 'volcano', 
      icon: <StopOutlined />, 
      text: 'Terminated' 
    }
  };

  const fetchJobs = useCallback(async (isBackgroundRefresh = false) => {
    // Allow null clientFilter to mean all clients
    // No early return; we can fetch all
     
    try {
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await executionAPI.getAllJobs(clientFilter);
      
      // Filter jobs: show running, pending, failed, waiting, resumed, and completed jobs from last 5 minutes
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      
      const filteredJobs = response.data.jobs.filter(job => {
        // Always show running, pending, failed, waiting, resumed, and terminated jobs
        if (['running', 'pending', 'failed', 'waiting', 'resumed', 'terminated'].includes(job.status)) {
          return true;
        }
        
        // For completed jobs, only show if completed within last 5 minutes
        if (job.status === 'completed' && job.completed_at) {
          const completedAt = parseTimestamp(job.completed_at);
          return completedAt && completedAt >= fiveMinutesAgo;
        }
        
        return false;
      });
      
      // Update jobs with smooth transition - preserve existing jobs and update/add new ones
      setJobs(prevJobs => {
        // Create a map of existing jobs for quick lookup
        const existingJobsMap = new Map(prevJobs.map(job => [job.id, job]));
        
        // Merge new data with existing, preserving order but updating content
        const updatedJobs = filteredJobs.map(newJob => {
          const existingJob = existingJobsMap.get(newJob.id);
          // If job exists and only progress/status changed, animate the change
          if (existingJob) {
            return {
              ...existingJob,
              ...newJob,
              // Add a flag to indicate this job was updated (could be used for animations)
              _updated: existingJob.progress !== newJob.progress || existingJob.status !== newJob.status
            };
          }
          return { ...newJob, _new: true };
        });
        
        return updatedJobs;
      });
      
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientFilter]);

  // Helper function to parse timestamps with proper timezone handling
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle potential timezone issues by treating timestamp as UTC if no timezone info
    if (timestamp.includes('T') && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
      // Timestamp appears to be UTC without timezone indicator, add 'Z' to ensure UTC parsing
      return new Date(timestamp + 'Z');
    } else {
      return new Date(timestamp);
    }
  };

  // Load clients for filter
  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await usersAPI.getUsers();
        setClients(res.data.filter(u => u.role === 'client'));
      } catch (e) {
        setClients([]);
      }
    };
    loadClients();
  }, []);

  // Sync prop selectedClient to filter if prop changes (and user hasn't chosen different yet)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedClient && selectedClient !== clientFilter) {
      setClientFilter(selectedClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  const showJobDetails = async (job) => {
    try {
      const response = await executionAPI.getJobDetails(job.id);
      setSelectedJob(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };

  // Keep the job detail modal updated while a job is running/waiting.
  useEffect(() => {
    if (!detailModalOpen || !selectedJob?.id) return;

    const pollableStatuses = new Set(['pending', 'running', 'waiting', 'resumed']);
    if (!pollableStatuses.has((selectedJob.status || '').toLowerCase())) return;

    let cancelled = false;
    const jobId = selectedJob.id;

    const refresh = async () => {
      try {
        const response = await executionAPI.getJobDetails(jobId);
        if (!cancelled) {
          setSelectedJob(response.data);
        }
      } catch (error) {
        // Silent in polling loop; the main UI already handles fetch errors.
      }
    };

    // Immediate refresh, then interval.
    refresh();
    const intervalId = setInterval(refresh, 2000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [detailModalOpen, selectedJob?.id, selectedJob?.status]);

  const handleDeleteJob = async (jobId, force = false) => {
    try {
      if (force) {
        await executionAPI.forceDeleteJob(jobId);
        message.success('Running job terminated and deleted');
      } else {
        await executionAPI.deleteJob(jobId);
        message.success('Job deleted');
      }
      // Remove job locally without waiting next refresh
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (e) {
      console.error('Failed to delete job', e);
      
      // Check if the error is because the job is running
      if (e.response?.data?.running && !force) {
        // Use a more reliable modal approach
        const confirmed = window.confirm(
          'Job is currently running\n\n' +
          'This job is currently running. Do you want to force terminate and delete it? ' +
          'This will also clean up any temporary files.\n\n' +
          'Click OK to force delete, or Cancel to abort.'
        );
        
        if (confirmed) {
          handleDeleteJob(jobId, true);
        }
      } else {
        message.error(e.response?.data?.error || 'Failed to delete job');
      }
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkDeleting(true);
    try {
      // Separate running and non-running jobs
      const selectedJobs = jobs.filter(j => selectedRowKeys.includes(j.id));
      const runningJobs = selectedJobs.filter(j => j.status === 'running');
      const nonRunningJobs = selectedJobs.filter(j => j.status !== 'running');
      
      // Delete non-running jobs first
      if (nonRunningJobs.length > 0) {
        await Promise.all(nonRunningJobs.map(j => executionAPI.deleteJob(j.id)));
      }
      
      // Handle running jobs if any
      if (runningJobs.length > 0) {
        // Show confirmation for running jobs
        Modal.confirm({
          title: `${runningJobs.length} running job(s) selected`,
          content: `${runningJobs.length} of the selected jobs are currently running. Do you want to force terminate and delete them? This will also clean up any temporary files.`,
          okText: 'Force Delete Running Jobs',
          okType: 'danger',
          cancelText: 'Skip Running Jobs',
          onOk: async () => {
            try {
              await Promise.all(runningJobs.map(j => executionAPI.forceDeleteJob(j.id)));
              message.success(`Deleted ${selectedJobs.length} job(s) (${runningJobs.length} terminated)`);
              setJobs(prev => prev.filter(j => !selectedJobs.some(d => d.id === j.id)));
              setSelectedRowKeys([]);
            } catch (e) {
              console.error('Bulk force delete failed', e);
              message.error('Some running jobs could not be terminated');
            }
          },
          onCancel: () => {
            if (nonRunningJobs.length > 0) {
              message.success(`Deleted ${nonRunningJobs.length} job(s) (skipped ${runningJobs.length} running job(s))`);
              setJobs(prev => prev.filter(j => !nonRunningJobs.some(d => d.id === j.id)));
              setSelectedRowKeys(runningJobs.map(j => j.id)); // Keep running jobs selected
            }
          }
        });
      } else {
        message.success(`Deleted ${nonRunningJobs.length} job(s)`);
        setJobs(prev => prev.filter(j => !selectedJobs.some(d => d.id === j.id)));
        setSelectedRowKeys([]);
      }
    } catch (e) {
      console.error('Bulk delete failed', e);
      message.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Auto-refresh effect with improved control
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    // Initial fetch
    fetchJobs(false);
    // Set up interval for background refreshes
    intervalRef.current = setInterval(() => {
      fetchJobs(true); // Background refresh
    }, refreshInterval);
    // Cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [clientFilter, refreshInterval, fetchJobs]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchJobs(false); // Full refresh with loading indicator
  };

  const renderProgress = (job) => {
    if (job.status === 'pending') {
      return <Progress percent={0} status="normal" size="small" />;
    }
    if (job.status === 'running') {
      return (
        <Progress 
          percent={job.progress || 0} 
          status="active" 
          size="small"
          strokeColor="#1890ff"
        />
      );
    }
    if (job.status === 'waiting') {
      return (
        <Progress 
          percent={job.progress || 0} 
          status="normal" 
          size="small"
          strokeColor="#faad14"
        />
      );
    }
    if (job.status === 'resumed') {
      return (
        <Progress 
          percent={job.progress || 0} 
          status="active" 
          size="small"
          strokeColor="#722ed1"
        />
      );
    }
    if (job.status === 'completed') {
      return <Progress percent={100} status="success" size="small" />;
    }
    if (job.status === 'failed') {
      return <Progress percent={job.progress || 0} status="exception" size="small" />;
    }
    return null;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = parseTimestamp(timestamp);
    return date ? date.toLocaleTimeString() : '-';
  };

  const formatDuration = (start, end) => {
    if (!start) return '-';
    const startTime = parseTimestamp(start);
    if (!startTime) return '-';
    
    const endTime = end ? parseTimestamp(end) : new Date();
    if (!endTime) return '-';
    
    const duration = Math.floor((endTime - startTime) / 1000);
    
    if (duration < 0) return '0s'; // Handle edge case where times might be inconsistent
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: 'Client',
      dataIndex: 'client_id',
      key: 'client',
      width: 140,
      render: (_, record) => {
        // Attempt multiple possible fields; fallback to lookup via clients list
        const direct = record.client_name || record.clientUsername || record.client || null;
        if (direct) return direct;
        const match = clients.find(c => c.id === record.client_id);
        return match ? match.username : '-';
      }
    },
    {
      title: 'Workflow',
      dataIndex: 'job_name',
      key: 'job_name',
      ellipsis: true,
      render: (text, record) => (
        <div>
      
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.workflow_name}
          </div>
        </div>
      )
    },
    {
      title: 'File',
      dataIndex: 'filename',
      key: 'filename',
      width: 150,
      ellipsis: true,
      render: (filename) => {
        if (!filename) {
          return (
            <Tooltip title="Manual job - no file">
              <span style={{ fontSize: '12px', fontStyle: 'italic', color: '#666' }}>
                manual
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={filename}>
            <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {filename}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 120,
      render: (_, record) => renderProgress(record)
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 100,
      render: formatTime
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => formatDuration(record.started_at, record.completed_at)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showJobDetails(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'running' ? 'Terminate and delete job' : 'Delete job'}>
            <Popconfirm
              title={record.status === 'running' ? 'Terminate and delete this running job?' : 'Delete this job?'}
              description={record.status === 'running' ? 'This will terminate the running job and clean up any temporary files. This cannot be undone.' : 'This cannot be undone.'}
              okType="danger"
              okText={record.status === 'running' ? 'Terminate & Delete' : 'Delete'}
              onConfirm={() => handleDeleteJob(record.id)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Count jobs by status for badge display
  const jobCounts = jobs.reduce((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1;
    return acc;
  }, {});

  // Function to recalc table height based on viewport
  const recalcTableHeight = useCallback(() => {
    if (!tableWrapperRef.current) return;
    const rect = tableWrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // Leave small margin at bottom (16px)
    const available = viewportHeight - rect.top - 16;
    if (available > 120) {
      setTableHeight(available);
    }
  }, []);

  useEffect(() => {
    recalcTableHeight();
    window.addEventListener('resize', recalcTableHeight);
    return () => window.removeEventListener('resize', recalcTableHeight);
  }, [recalcTableHeight]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ disabled: record.status === 'running' }),
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Job Monitor
              {refreshing && (
                <Spin 
                  indicator={<LoadingOutlined style={{ fontSize: 14, marginLeft: 8 }} spin />} 
                />
              )}
              <Divider type="vertical" />
              <Select
                size="small"
                allowClear
                placeholder="All Clients"
                style={{ minWidth: 160 }}
                value={clientFilter || undefined}
                onChange={(val) => setClientFilter(val || null)}
                showSearch
                optionFilterProp="children"
              >
                {clients.map(c => (
                  <Select.Option key={c.id} value={c.id}>{c.username}</Select.Option>
                ))}
              </Select>
            </span>
            <Space wrap>
              {jobCounts.running > 0 && (
                <Badge count={jobCounts.running} color="blue">
                  <Tag color="blue">Running</Tag>
                </Badge>
              )}
              {jobCounts.waiting > 0 && (
                <Badge count={jobCounts.waiting} color="gold">
                  <Tag color="gold">Waiting</Tag>
                </Badge>
              )}
              {jobCounts.failed > 0 && (
                <Badge count={jobCounts.failed} color="red">
                  <Tag color="red">Failed</Tag>
                </Badge>
              )}
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} selected job(s)?`}
                  description="Running jobs will be terminated before deletion."
                  okType="danger"
                  okText="Delete All"
                  onConfirm={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  <Button
                    size="small"
                    danger
                    loading={bulkDeleting}
                  >Delete Selected</Button>
                </Popconfirm>
              )}
              <Tooltip title="Refresh jobs">
                <Button
                  size="small"
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={handleManualRefresh}
                  loading={loading && !refreshing}
                />
              </Tooltip>
            </Space>
          </div>
        }
        size="small"
        style={{ marginBottom: 16, flex: 1, display: 'flex', flexDirection: 'column' }}
        className={refreshing ? 'job-monitor-refreshing' : ''}
      >
        {
           <div ref={tableWrapperRef} style={{ position: 'relative', flex: 1, display: 'flex' }}>
             <Table
               columns={columns}
               dataSource={jobs}
               loading={loading && !refreshing}
               rowKey="id"
               size="small"
               pagination={false}
               scroll={{ y: tableHeight - 8 }}
               style={{ flex: 1 }}
               rowSelection={rowSelection}
               locale={{
                 emptyText: loading ? 'Loading jobs...' : 'No active jobs'
               }}
               rowClassName={(record) => {
                 // Add subtle animation for updated rows
                 if (record._updated) {
                   return 'job-row-updated';
                 }
                 if (record._new) {
                   return 'job-row-new';
                 }
                 return '';
               }}
               onHeaderRow={() => {
                 // Recalc in case header height changes (e.g., responsive)
                 setTimeout(recalcTableHeight, 0);
               }}
             />
             {refreshing && !loading && (
               <div 
                 style={{
                   position: 'absolute',
                   top: 0,
                   right: 0,
                   background: 'rgba(24, 144, 255, 0.1)',
                   padding: '4px 8px',
                   borderRadius: '4px',
                   fontSize: '12px',
                   color: '#1890ff',
                   zIndex: 10
                 }}
               >
                 Updating...
               </div>
             )}
           </div>
        }
      </Card>

      <Modal
        title={`Job Details: ${selectedJob?.job_name}`}
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedJob(null);
        }}
        footer={null}
        width={600}
      >
        {selectedJob && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color={statusConfig[selectedJob.status]?.color || 'default'} 
                   icon={statusConfig[selectedJob.status]?.icon}>
                {statusConfig[selectedJob.status]?.text || selectedJob.status}
              </Tag>
              {selectedJob.progress !== null && (
                <span style={{ marginLeft: 8 }}>
                  Progress: {selectedJob.progress}%
                </span>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Workflow:</strong> {selectedJob.workflow_name}
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>File:</strong>
              {selectedJob.filename ? (
                <div style={{ 
                  background: '#f5f5f5', 
                  border: '1px solid #d9d9d9', 
                  padding: 8, 
                  borderRadius: 4,
                  marginTop: 4,
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  {selectedJob.filename}
                </div>
              ) : (
                <div style={{ 
                  background: '#f9f9f9', 
                  border: '1px solid #e8e8e8', 
                  padding: 8, 
                  borderRadius: 4,
                  marginTop: 4,
                  fontSize: '12px',
                  fontStyle: 'italic',
                  color: '#666'
                }}>
                  Manual job - no file associated
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Started:</strong> {selectedJob.started_at ? 
                parseTimestamp(selectedJob.started_at)?.toLocaleString() || 'Invalid date' : 'Not started'}
            </div>

            {selectedJob.completed_at && (
              <div style={{ marginBottom: 16 }}>
                <strong>Completed:</strong> {parseTimestamp(selectedJob.completed_at)?.toLocaleString() || 'Invalid date'}
              </div>
            )}

            {selectedJob.error_message && (
              <div style={{ marginBottom: 16 }}>
                <strong>Error:</strong>
                <div style={{ 
                  background: '#fff2f0', 
                  border: '1px solid #ffccc7', 
                  padding: 8, 
                  borderRadius: 4,
                  marginTop: 4,
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  {selectedJob.error_message}
                </div>
              </div>
            )}

            <div>
              <strong>Execution Log:</strong>
              <div style={{ marginTop: 8 }}>
                <ExecutionLogViewer executionLog={selectedJob.execution_log} jobId={selectedJob.id} />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobMonitor;
